import { DecryptionFailureCode } from "matrix-js-sdk/lib/crypto-api";

export const MATRIX_KEY_RECOVERY_STATE = {
    RECOVERING: "recovering",
    WITHHELD: "withheld",
    EXHAUSTED: "exhausted",
};

export const MATRIX_KEY_RECOVERY_PROPERTY = "__dailyfixmeKeyRecoveryState";
export const MATRIX_KEY_RECOVERY_RETRY_DELAYS_MS = [750, 2000, 5000, 10000, 20000, 30000];

const ROOM_KEY_REQUEST_EVENT_TYPE = "m.room_key_request";
const MEGOLM_ALGORITHM = "m.megolm.v1.aes-sha2";

const createRequestId = () => {
    if (typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${crypto.getRandomValues(new Uint32Array(4)).join("-")}`;
};

const createToDeviceMap = (recipients, content) => {
    const contentMap = new Map();
    recipients.forEach(({ userId, deviceId }) => {
        if (!contentMap.has(userId)) {
            contentMap.set(userId, new Map());
        }
        contentMap.get(userId).set(deviceId, content);
    });
    return contentMap;
};

export const requestRoomKey = async (matrixClient, matrixEvent) => {
    const wireContent = matrixEvent?.getWireContent?.();
    const roomId = matrixEvent?.getRoomId?.();
    const userId = matrixClient?.getUserId?.();
    const deviceId = matrixClient?.getDeviceId?.();
    if (
        !roomId ||
        !userId ||
        !deviceId ||
        wireContent?.algorithm !== MEGOLM_ALGORITHM ||
        typeof wireContent?.sender_key !== "string" ||
        typeof wireContent?.session_id !== "string"
    ) {
        throw new Error("The encrypted event does not contain a valid Megolm key request body.");
    }

    const recipients = matrixEvent.getKeyRequestRecipients(userId);
    const requestId = createRequestId();
    const requestBody = {
        algorithm: wireContent.algorithm,
        room_id: roomId,
        sender_key: wireContent.sender_key,
        session_id: wireContent.session_id,
    };
    const content = {
        action: "request",
        body: requestBody,
        request_id: requestId,
        requesting_device_id: deviceId,
    };

    await matrixClient.sendToDevice(
        ROOM_KEY_REQUEST_EVENT_TYPE,
        createToDeviceMap(recipients, content)
    );
    return { requestId, recipients, requestingDeviceId: deviceId };
};

export const cancelRoomKeyRequest = async (matrixClient, request) => {
    if (!matrixClient || !request) {
        return;
    }
    const content = {
        action: "request_cancellation",
        request_id: request.requestId,
        requesting_device_id: request.requestingDeviceId,
    };
    await matrixClient.sendToDevice(
        ROOM_KEY_REQUEST_EVENT_TYPE,
        createToDeviceMap(request.recipients, content)
    );
};

export const setMatrixKeyRecoveryState = (matrixEvent, state) => {
    if (matrixEvent) {
        matrixEvent[MATRIX_KEY_RECOVERY_PROPERTY] = state;
    }
};

export const getMatrixKeyRecoveryState = (matrixEvent) => {
    return matrixEvent?.[MATRIX_KEY_RECOVERY_PROPERTY] || null;
};

export const retryExistingEncryptedEvents = async (matrixClient, handleFailure) => {
    const cryptoApi = matrixClient?.getCrypto?.();
    if (!cryptoApi) {
        return;
    }

    const encryptedEvents = (matrixClient.getRooms?.() || []).flatMap(room => {
        return room.getLiveTimeline?.().getEvents?.().filter(event => {
            return event.getWireType?.() === "m.room.encrypted" && !event.isDecrypted?.();
        }) || [];
    });

    for (const event of encryptedEvents) {
        try {
            await event.attemptDecryption(cryptoApi, { isRetry: true });
        } catch (error) {
            handleFailure(event, error);
        }
    }
};

export const isPermanentlyWithheldRoomKey = (error, matrixEvent) => {
    const reason = matrixEvent?.decryptionFailureReason;
    const errorText = `${error?.message || ""} ${error?.code || ""}`;
    return reason === DecryptionFailureCode.MEGOLM_KEY_WITHHELD ||
        reason === DecryptionFailureCode.MEGOLM_KEY_WITHHELD_FOR_UNVERIFIED_DEVICE ||
        errorText.includes(DecryptionFailureCode.MEGOLM_KEY_WITHHELD) ||
        errorText.includes("withheld");
};

export const isRetryableMissingRoomKey = (error, matrixEvent) => {
    const reason = matrixEvent?.decryptionFailureReason;
    const errorText = `${error?.message || ""} ${error?.code || ""} ${error?.name || ""}`;
    return reason === DecryptionFailureCode.MEGOLM_UNKNOWN_INBOUND_SESSION_ID ||
        errorText.includes("sender's device has not sent us the keys") ||
        errorText.includes(DecryptionFailureCode.MEGOLM_UNKNOWN_INBOUND_SESSION_ID) ||
        errorText.includes("MissingRoomKey");
};
